import { useEffect } from 'react';
import { Minus, Plus } from '../../../../modules/design-system';

export interface QuantityStepperProps {
  value: number;
  min?: number;
  max?: number;
  onChange: (next: number) => void;
  /** md — Figma mobile 35:15118, lg — Figma desktop 17:5533. */
  size?: 'md' | 'lg';
}

const SIZES = {
  md: {
    pod: 'h-[60px] w-[148px] rounded-[36px]',
    btn: 'h-11 w-11',
    icon: 20,
    number: 'text-[20px] leading-[24px]',
  },
  lg: {
    pod: 'h-[64px] w-[156px] rounded-[38px]',
    btn: 'h-[47px] w-[47px]',
    icon: 21,
    number: 'text-[21px] leading-[26px]',
  },
} as const;

/**
 * [−]  N  [+]. Two layers per Figma:
 *   • Outer white pod (the thicker "collar" around the button)
 *   • Inner gradient button (E0E0E0 → FFFFFF) — 4px inset from the pod
 *
 * The two circular ± buttons sit on top of the inner gradient, each with
 * a thin white border and a hair-line drop shadow. The number in the
 * middle uses Inter ExtraBold with tabular-nums so the digit width
 * doesn't jump (1 → 2 → 10).
 */
export function QuantityStepper({
  value,
  min = 1,
  max = 99,
  onChange,
  size = 'md',
}: QuantityStepperProps) {
  const s = SIZES[size];
  const canDec = value > min;
  const canInc = value < max;

  // Keep the value inside [min, max]. Mirrors web QuantityPicker.
  //  - Value below min (e.g. 0 from an earlier clamp) → bump to min.
  //  - Value above max (e.g. stock refresh lowered the cap) → clamp down.
  //  - While max is still loading (< min), leave value alone — don't drag
  //    it to 0 and lock the stepper out.
  useEffect(() => {
    if (value < min) {
      onChange(min);
      return;
    }
    if (max >= min && value > max) {
      onChange(max);
    }
  }, [value, min, max, onChange]);

  const btnClass =
    `${s.btn} shrink-0 rounded-full cursor-pointer ` +
    'border border-white ' +
    '[background-image:linear-gradient(180deg,rgba(174,174,174,0.2)_0%,rgba(255,255,255,0.2)_100%)] ' +
    '[box-shadow:0_0_1px_0_rgba(0,0,0,0.25)] ' +
    'flex items-center justify-center transition-transform active:scale-95 ' +
    'disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100';

  return (
    <div
      data-co-component="QuantityStepper"
      className={
        s.pod +
        ' relative shrink-0 ' +
        '[box-shadow:0_0_1px_0_rgba(0,0,0,0.25)]'
      }
      style={{ background: 'var(--co-QuantityStepper-containerFill, #ffffff)' }}
    >
      {/* Inner gradient button surface — 4-5px inset from the outer pod.
          Operator-controlled via --co-QuantityStepper-backgroundFrom/To. */}
      <div
        aria-hidden
        className="absolute inset-[4px] rounded-[inherit] border border-[#F2F2F2]"
        style={{
          backgroundImage:
            'linear-gradient(180deg, ' +
            'var(--co-QuantityStepper-backgroundFrom, #E0E0E0) 0%, ' +
            'var(--co-QuantityStepper-backgroundTo, #FFFFFF) 100%)',
        }}
      />

      {/* Buttons + number */}
      <div className="absolute inset-[5px] flex items-center justify-between">
        <button
          type="button"
          onClick={() => canDec && onChange(value - 1)}
          disabled={!canDec}
          aria-label="Decrease quantity"
          className={btnClass}
          style={{
            backgroundColor: 'var(--co-QuantityStepper-buttonFill, #ffffff)',
            color: 'var(--co-QuantityStepper-buttonIcon, #15181E)',
          }}
        >
          <Minus size={s.icon} />
        </button>

        <span
          className={
            s.number +
            ' font-extrabold tracking-[-0.3px] ' +
            "font-['Inter','Chivo_Mono',ui-monospace,monospace] tabular-nums"
          }
          style={{
            color: 'var(--co-QuantityStepper-valueText, var(--color-text-primary, #15181E))',
          }}
        >
          {value}
        </span>

        <button
          type="button"
          onClick={() => canInc && onChange(value + 1)}
          disabled={!canInc}
          aria-label="Increase quantity"
          className={btnClass}
          style={{
            backgroundColor: 'var(--co-QuantityStepper-buttonFill, #ffffff)',
            color: 'var(--co-QuantityStepper-buttonIcon, #15181E)',
          }}
        >
          <Plus size={s.icon} />
        </button>
      </div>
    </div>
  );
}
